import { useEffect, useRef } from 'react';

import { getDataSourceStatus } from '../services/api';

const isPendingStatus = (status) => {
  return (
    !status ||
    status === 'queued' ||
    status === 'processing' ||
    status.startsWith('failed ')
  );
};

const useFileStatusPolling = (
  allUserDataSourceFiles,
  userGoogleId,
  setAllUserDataSourceFiles,
  setError,
) => {
  const pollingIntervals = useRef({}); // Store intervals for each file
  const consecutiveSameStatusCount = useRef({}); // Track consecutive status counts for each file
  const baseInterval = 10000; // Base polling interval (10 seconds)

  // Function to poll status for a specific file
  const pollFileStatus = async (file) => {
    const file_id = file.file_id;

    try {
      const response = await getDataSourceStatus(file_id, userGoogleId);
      if (response.error) {
        setError(response.error);
        stopPollingFile(file_id); // Stop polling on error
      } else {
        if (file.status !== response.status) {
          // Status changed, reset consecutive count and update state
          consecutiveSameStatusCount.current[file_id] = 0;

          setAllUserDataSourceFiles((previousFiles) => {
            return previousFiles.map((f) => {
              if (f.file_id === file_id) {
                f.status = response.status;
              }
              return f;
            });
          });

          // Stop polling if the status is final (success or final failed)
          if (response.status === 'success' || response.status === 'failed') {
            stopPollingFile(file_id);
          } else {
            // Reset polling interval to base interval when status changes
            updatePollingInterval(file, baseInterval);
          }
        } else {
          // Status is the same as before, apply exponential backoff
          consecutiveSameStatusCount.current[file_id] =
            (consecutiveSameStatusCount.current[file_id] || 0) + 1;
          const newInterval =
            baseInterval *
            Math.pow(2, consecutiveSameStatusCount.current[file_id]);
          updatePollingInterval(file, newInterval); // Update polling interval with exponential backoff
        }
      }
    } catch (error) {
      setError('Error fetching file status');
      stopPollingFile(file_id);
    }
  };

  // Function to update the polling interval for a file
  const updatePollingInterval = (file, newInterval) => {
    const file_id = file.file_id;
    if (pollingIntervals.current[file_id]) {
      clearInterval(pollingIntervals.current[file_id]); // Clear existing interval
    }
    pollingIntervals.current[file_id] = setInterval(
      () => pollFileStatus(file),
      newInterval,
    ); // Set new interval
  };

  // Setup polling for a file if it's in a pending state
  const startPollingFile = (file) => {
    const file_id = file.file_id;
    if (isPendingStatus(file.status) && !pollingIntervals.current[file_id]) {
      consecutiveSameStatusCount.current[file_id] = 0; // Initialize consecutive count
      pollingIntervals.current[file_id] = setInterval(
        () => pollFileStatus(file),
        baseInterval,
      ); // Start polling with base interval
    }
  };

  // Stop polling for a file
  const stopPollingFile = (file_id) => {
    if (pollingIntervals.current[file_id]) {
      clearInterval(pollingIntervals.current[file_id]);
      delete pollingIntervals.current[file_id];
      delete consecutiveSameStatusCount.current[file_id]; // Clear consecutive count
    }
  };

  // Monitor changes in allUserDataSourceFiles to start/stop polling
  useEffect(() => {
    const intervals = pollingIntervals.current;

    // Start polling
    allUserDataSourceFiles.forEach((file) => {
      startPollingFile(file);
    });

    return () => {
      Object.keys(intervals).forEach((file_id) => {
        stopPollingFile(file_id);
      });
    };
    // eslint-disable-next-line
  }, [allUserDataSourceFiles]);
};

export default useFileStatusPolling;
